import { useContext, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import Button from '../../../../common/base/Button';
import { StyledCard } from '../../../../common/base/Card';
import { Div } from '../../../../common/helpers/StyledUtils';
import Text from '../../../../common/base/Text';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { ChangelogEntry } from './useChangelog';
import Icon from '../../../../common/base/Icon';

const StyledChangelogImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const StyledPicture = styled.picture`
  display: block;
  width: 100%;
`;

const ImagePlaceholder = styled.div<{ aspect_ratio: number; is_loaded: boolean }>(
  ({ theme, aspect_ratio, is_loaded }) => css`
    position: relative;
    width: 100%;
    padding-bottom: ${(1 / aspect_ratio) * 100}%;
    background-color: ${theme.colors.surface.base.variant_surface_3};
    margin-top: 16px;
    border-radius: ${theme.radius.normal};
    overflow: hidden;

    ${StyledPicture} {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    ${is_loaded &&
    css`
      background-color: transparent;
    `}
  `,
);

const ChangelogCard = ({
  entry,
  onDismiss,
  onOpenFeed,
}: {
  entry: ChangelogEntry;
  onDismiss: () => void;
  onOpenFeed: () => void;
}) => {
  const { HookdeckAPI } = useContext(GlobalContext);
  const theme = useTheme();
  const [is_image_loaded, setIsImageLoaded] = useState(false);

  const handleOpenDropdown = async () => {
    // Update the user's last_seen_changelog timestamp
    await HookdeckAPI.session.updateMe({
      last_seen_changelog: new Date().toISOString(),
    });
    onOpenFeed();
  };

  const {
    title,
    short_title,
    description,
    image_src_set,
    image_dimensions,
    card_image_light_src_set,
    card_image_dark_src_set,
    card_image_dimensions,
  } = entry;

  // Determine which card image to use based on theme
  const is_dark_mode = theme.mode === 'dark';
  const card_image_src_set = is_dark_mode ? card_image_dark_src_set : card_image_light_src_set;

  // Use card image if available, otherwise fall back to main image
  const display_image_src_set = card_image_src_set || image_src_set;
  const display_image_dimensions = card_image_src_set ? card_image_dimensions : image_dimensions;

  return (
    <StyledCard p={3}>
      <Div flex={{ align: 'center', gap: 1 }}>
        <Icon icon="new_releases" primary />
        <Text semi>{short_title || title}</Text>
        <Button ghost small icon="close" onClick={onDismiss} m={{ l: 'auto' }} />
      </Div>
      <Text muted size="s" m={{ t: 1 }}>
        {description}
      </Text>
      {display_image_src_set && display_image_dimensions && (
        <ImagePlaceholder
          aspect_ratio={display_image_dimensions.aspect_ratio}
          is_loaded={is_image_loaded}>
          <StyledPicture>
            <source
              srcSet={`
              ${display_image_src_set['1x']} 1x,
              ${display_image_src_set['2x'] || display_image_src_set['1x']} 2x,
              ${display_image_src_set['3x'] || display_image_src_set['2x'] || display_image_src_set['1x']} 3x
            `.trim()}
            />
            <StyledChangelogImage
              src={
                display_image_src_set.default ||
                display_image_src_set['2x'] ||
                display_image_src_set['1x']
              }
              alt={short_title || title}
              loading="lazy"
              onLoad={() => setIsImageLoaded(true)}
            />
          </StyledPicture>
        </ImagePlaceholder>
      )}
      <Button outline block m={{ t: 3 }} onClick={handleOpenDropdown}>
        View changelog
      </Button>
    </StyledCard>
  );
};

export default ChangelogCard;
